'use client';

// Packages
import { useRef, useState, useEffect, useCallback } from 'react';
import { useBlockingRouter } from '../../hooks/useBlockingRouter';
import { useDispatch } from 'react-redux';
import { gql } from '@apollo/client';
import rpcShared from '@rockpapercoin/rpc-shared';

// Logic
import validations from '../../lib/helpers/validations';
import {
	useLoginPage_LoginLazyQuery,
	useLoginPage_CreateMfaTokenMutation,
	useLoginPage_RequestResetMutation,
} from '#types';
import { useToast } from '../../hooks/useToast';
import { login, toggleLoadingOverlay } from '../../redux/actions';
import { segmentGroup, segmentIdentify } from '../../lib/helpers/segment';
import { getAuthedUserForRedux } from '../../lib/helpers/getAuthedUserForRedux';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const loginMutation = gql`
	query loginPage_login($data: UserLoginInput!) {
		login(data: $data) {
			token
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createImpersonationTokenMutation = gql`
	mutation loginPage_createMfaToken($data: MfaTokenInput!) {
		createMfaToken(data: $data)
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const requestResetMutation = gql`
	mutation loginPage_requestReset($data: PasswordResetRequestInput!) {
		requestReset(data: $data)
	}
`;

export type LoginFormValidation = {
	email?: string;
	password?: string;
	mfaToken?: string;
	login?: string;
};

export type UseLoginProps = {
	returnTo?: string;
	impersonateUser?: string;
};

export const useLogin = ( { returnTo, impersonateUser }: UseLoginProps ) => {
	const { showError, showSuccess } = useToast();

	const [ mfaToken, setMfaToken ] = useState( '' );
	const [ showMfaModal, setShowMfaModal ] = useState( false );
	const [ password, setPassword ] = useState( '' );
	const [ email, setEmail ] = useState( '' );
	const [ validation, setValidation ] = useState<LoginFormValidation>( {} );
	const [ passwordExpired, setPasswordExpired ] = useState( false );
	const [ loginQuery, { loading: loginLoading } ] = useLoginPage_LoginLazyQuery();
	const [ requestReset, { loading: requestResetLoading } ] =
		useLoginPage_RequestResetMutation();
	const [ createMfaToken, { loading: mfaLoading } ] =
		useLoginPage_CreateMfaTokenMutation();
	const router = useBlockingRouter();
	const dispatch = useDispatch();
	const validationEnabledRef = useRef( false );

	const onValidate = ( force?: boolean ) => {
		if ( force ) {
			validationEnabledRef.current = true;
		}
		const showValidation = validationEnabledRef.current || force;
		if ( !showValidation ) {
			return true;
		}
		const newValidation = {
			email: validations.validateEmail( email ),
			password: password ? undefined : 'Please enter your password',
		};
		setValidation( newValidation );
		if ( Object.values( newValidation ).some( ( v ) => v ) ) {
			return false;
		}
		return true;
	};

	const onImpersonate = useCallback( async () => {
		const res = await createMfaToken( {
			variables: {
				data: {
					email,
				},
			},
		} );
		if ( res.errors ) {
			if (
				res.errors.some( ( error ) =>
					error.message.includes(
						rpcShared.strings.errorMessages.noImpersonationPermission
					)
				)
			) {
				showError( 'Oops, something went wrong, Please let us know!' );
			} else {
				res.errors.map( ( error ) => showError( error.message ) );
			}
		} else if ( res.data?.createMfaToken ) {
			setShowMfaModal( true );
		}
	}, [
		createMfaToken,
		email,
		showError
	] );

	const onLogin = useCallback( async () => {
		dispatch( toggleLoadingOverlay( true ) );
		setPasswordExpired( false );
		const loginRes = await loginQuery( {
			variables: {
				data: {
					email,
					password,
					...( mfaToken && impersonateUser
						? {
							mfaToken: parseInt( mfaToken ),
							targetUserEmail: impersonateUser,
						  }
						: {} ),
				},
			},
			errorPolicy: 'none',
		} );
		if ( loginRes.error ) {
			const remainingErrors = rpcShared.errorFiltering.filterApiErrors(
				loginRes,
				[
					'passwordExpired',
					'invalidPassword',
					'userNotFound'
				],
				{
					passwordExpired: () => setPasswordExpired( true ),
					invalidPassword: () =>
						setValidation( {
							...validation,
							password: 'You entered an incorrect email, password, or both.',
						} ),
					userNotFound: () =>
						setValidation( {
							...validation,
							password: 'You entered an incorrect email, password, or both.',
						} ),
				}
			);
			if ( remainingErrors.error ) {
				showError( remainingErrors.error );
			}
		} else if ( loginRes.data?.login ) {
			const userForRedux = await getAuthedUserForRedux(
				impersonateUser || email,
				loginRes.data.login.token
			);
			if ( userForRedux ) {
				dispatch( login( userForRedux, loginRes.data.login.token ) );
				segmentIdentify( userForRedux );
				segmentGroup( userForRedux );
				setShowMfaModal( false );
				setValidation( {} );
				setPassword( '' );
				setEmail( '' );
				setMfaToken( '' );
				router.push( returnTo || '/' );
			}
		}
		setTimeout( () => dispatch( toggleLoadingOverlay( false ) ), 2000 );
	}, [
		dispatch,
		email,
		impersonateUser,
		loginQuery,
		mfaToken,
		returnTo,
		password,
		router,
		validation,
		showError,
	] );

	const onRequestReset = useCallback( async () => {
		const res = await requestReset( { variables: { data: { email } } } );
		if ( res.errors ) {
			res.errors.forEach( ( error ) => showError( error.message ) );
		} else {
			showSuccess(
				`An email will be sent to ${ email } if there is a user associated with that address.`
			);
			setPasswordExpired( false );
		}
	}, [
		email,
		requestReset,
		showError,
		showSuccess
	] );

	const handleLogin = async ( e: React.FormEvent<HTMLFormElement> ) => {
		e.preventDefault();
		validationEnabledRef.current = true;
		if ( onValidate() ) {
			if ( impersonateUser && !mfaToken ) {
				onImpersonate();
			} else {
				onLogin();
			}
		}
	};

	useEffect( () => {
		// Prefetch the dashboard page
		const postLoginLocation = returnTo || '/';
		router.prefetch( postLoginLocation );
	}, [ router, returnTo ] );

	return {
		email,
		setEmail,
		password,
		setPassword,
		mfaToken,
		setMfaToken,
		validation,
		onValidate,
		showMfaModal,
		setShowMfaModal,
		passwordExpired,
		setPasswordExpired,
		loginLoading,
		mfaLoading,
		requestResetLoading,
		onLogin,
		onRequestReset,
		handleLogin,
	};
};
